/**
 * useInvoiceActions-helpers — sub-hooks and helpers for useInvoiceActions to
 * keep the main hook body under the lint cap.
 */

import { useState, useCallback, useRef } from 'react'

import { sanitizeAddress, buildItemsPayload, buildSharedExportFields } from './invoices-helpers'
import { type Toast } from '../../components/ui'
import {
  getInvoice,
  createInvoice,
  updateInvoice,
  editPostedInvoice,
} from '../../services'
import {
  writeOffInvoiceBalance,
  reverseWriteOff,
  getInvoicePayments,
  type InvoicePaymentsResponse,
} from '../../services/payment-service'

import type { Invoice, InvoiceFormState } from '../../types/billing'

// ── Payments fetching ──────────────────────────────────────────────────────

interface PaymentsState {
  invoicePayments: InvoicePaymentsResponse | null
  isInvoicePaymentsLoading: boolean
  fetchInvoicePayments: (invoiceId: string) => Promise<void>
}

export function useInvoicePaymentsState(organizationId: string): PaymentsState {
  const [invoicePayments, setInvoicePayments] = useState<InvoicePaymentsResponse | null>(null)
  const [isInvoicePaymentsLoading, setIsInvoicePaymentsLoading] = useState(false)
  const fetchInvoicePayments = useCallback(async (invoiceId: string) => {
    setIsInvoicePaymentsLoading(true)
    try {
      const resp = await getInvoicePayments(organizationId, invoiceId)
      if (resp.data) { setInvoicePayments(resp.data) }
    } catch {
      // silently ignore
    } finally {
      setIsInvoicePaymentsLoading(false)
    }
  }, [organizationId])
  return { invoicePayments, isInvoicePaymentsLoading, fetchInvoicePayments }
}

// ── Write-off state and handlers ───────────────────────────────────────────

interface WriteOffStateArgs {
  selectedInvoice: Invoice | null
  organizationId: string
  setSelectedInvoice: (inv: Invoice | null) => void
  fetchInvoicePayments: (id: string) => Promise<void>
  addToast: (t: Omit<Toast, 'id'>) => void
}

interface WriteOffStateResult {
  isWriteOffSaving: boolean
  isWriteOffConfirmVisible: boolean
  setIsWriteOffConfirmVisible: (v: boolean) => void
  writeOffType: 'rounding' | 'bad_debt'
  setWriteOffType: (t: 'rounding' | 'bad_debt') => void
  writeOffNote: string
  setWriteOffNote: (n: string) => void
  handleWriteOff: () => Promise<void>
  handleReverseWriteOff: () => Promise<void>
  confirmingAction: 'rounding' | 'bad_debt' | null
  setConfirmingAction: (a: 'rounding' | 'bad_debt' | null) => void
  confirmTimerRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>
}

interface WriteOffOpArgs {
  selectedInvoice: Invoice
  organizationId: string
  setSelectedInvoice: (inv: Invoice | null) => void
  fetchInvoicePayments: (id: string) => Promise<void>
  addToast: (t: Omit<Toast, 'id'>) => void
}

async function runWriteOff(
  ctx: WriteOffOpArgs, writeOffType: 'rounding' | 'bad_debt', writeOffNote: string,
): Promise<void> {
  const defaultNote = writeOffType === 'rounding' ? 'Rounding adjustment' : 'Bad debt write-off'
  try {
    const resp = await writeOffInvoiceBalance(ctx.organizationId, ctx.selectedInvoice.id,
      writeOffNote.trim() || defaultNote, writeOffType)
    if (resp.data) {
      ctx.addToast({ variant: 'success', message: `Balance written off as ${writeOffType === 'rounding' ? 'rounding' : 'bad debt'}` })
      const invResp = await getInvoice(ctx.organizationId, ctx.selectedInvoice.id)
      if (invResp.data) { ctx.setSelectedInvoice(invResp.data) }
      void ctx.fetchInvoicePayments(ctx.selectedInvoice.id)
    } else if (resp.error) {
      ctx.addToast({ variant: 'error', message: resp.error.message ?? 'Write-off failed' })
    }
  } catch {
    ctx.addToast({ variant: 'error', message: 'Write-off failed' })
  }
}

async function runReverseWriteOff(ctx: WriteOffOpArgs): Promise<void> {
  try {
    const resp = await reverseWriteOff(ctx.organizationId, ctx.selectedInvoice.id, 'Reversed from invoice detail')
    if (resp.data) {
      ctx.addToast({ variant: 'success', message: 'Write-off reversed successfully' })
      const invResp = await getInvoice(ctx.organizationId, ctx.selectedInvoice.id)
      if (invResp.data) { ctx.setSelectedInvoice(invResp.data) }
      void ctx.fetchInvoicePayments(ctx.selectedInvoice.id)
    } else if (resp.error) {
      ctx.addToast({ variant: 'error', message: resp.error.message ?? 'Reverse failed' })
    }
  } catch {
    ctx.addToast({ variant: 'error', message: 'Reverse write-off failed' })
  }
}

export function useWriteOffActions(args: WriteOffStateArgs): WriteOffStateResult {
  const { selectedInvoice, organizationId, setSelectedInvoice, fetchInvoicePayments, addToast } = args
  const [isWriteOffSaving, setIsWriteOffSaving] = useState(false)
  const [isWriteOffConfirmVisible, setIsWriteOffConfirmVisible] = useState(false)
  const [writeOffType, setWriteOffType] = useState<'rounding' | 'bad_debt'>('rounding')
  const [writeOffNote, setWriteOffNote] = useState('')
  const [confirmingAction, setConfirmingAction] = useState<'rounding' | 'bad_debt' | null>(null)
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleWriteOff = useCallback(async () => {
    if (!selectedInvoice) { return }
    setIsWriteOffSaving(true)
    await runWriteOff({ selectedInvoice, organizationId, setSelectedInvoice, fetchInvoicePayments, addToast }, writeOffType, writeOffNote)
    setIsWriteOffSaving(false)
    setIsWriteOffConfirmVisible(false)
    setWriteOffNote('')
  }, [selectedInvoice, organizationId, writeOffType, writeOffNote, addToast, fetchInvoicePayments, setSelectedInvoice])

  const handleReverseWriteOff = useCallback(async () => {
    if (!selectedInvoice) { return }
    setIsWriteOffSaving(true)
    await runReverseWriteOff({ selectedInvoice, organizationId, setSelectedInvoice, fetchInvoicePayments, addToast })
    setIsWriteOffSaving(false)
  }, [selectedInvoice, organizationId, addToast, fetchInvoicePayments, setSelectedInvoice])

  return {
    isWriteOffSaving, isWriteOffConfirmVisible, setIsWriteOffConfirmVisible,
    writeOffType, setWriteOffType, writeOffNote, setWriteOffNote,
    handleWriteOff, handleReverseWriteOff,
    confirmingAction, setConfirmingAction, confirmTimerRef,
  }
}

// ── Submit (existing/new) ──────────────────────────────────────────────────

interface SubmitExistingParams {
  formState: InvoiceFormState
  selectedInvoice: Invoice
  organizationId: string
  addToast: (t: Omit<Toast, 'id'>) => void
  fetchInvoices: () => Promise<void>
  navigate: (path: string) => void
  basePath: string
}

function buildEditPostedPayload(formState: InvoiceFormState, exportFields: ReturnType<typeof buildSharedExportFields>): Parameters<typeof editPostedInvoice>[2] {
  return {
    invoiceNumber: formState.invoiceNumber,
    supplierBillNumber: formState.supplierBillNumber,
    invoiceDate: formState.invoiceDate,
    dueDate: formState.dueDate,
    placeOfSupply: formState.placeOfSupply,
    placeOfSupplyCode: formState.placeOfSupplyCode,
    currency: formState.currency,
    exchangeRate: formState.exchangeRate,
    poNumber: formState.poNumber,
    lutNumber: formState.lutNumber,
    lutDate: formState.lutDate,
    portCode: formState.portCode,
    paymentTerms: formState.paymentTerms,
    bankDetails: formState.bankDetails,
    notes: formState.notes,
    termsConditions: formState.termsConditions,
    roundOffAmount: formState.roundOffAmount,
    changeNotes: formState.changeNotes ?? 'Invoice edited',
    ...exportFields,
    items: buildItemsPayload(formState.items),
  }
}

function buildUpdatePayload(formState: InvoiceFormState, exportFields: ReturnType<typeof buildSharedExportFields>): Parameters<typeof updateInvoice>[2] {
  return {
    invoiceNumber: formState.invoiceNumber,
    supplierBillNumber: formState.supplierBillNumber,
    invoiceDate: formState.invoiceDate,
    dueDate: formState.dueDate,
    partyId: formState.partyId,
    partyRole: formState.partyRole,
    partyAddressId: formState.partyAddressId,
    partyAddress: sanitizeAddress(formState.partyAddress),
    placeOfSupply: formState.placeOfSupply,
    placeOfSupplyCode: formState.placeOfSupplyCode,
    currency: formState.currency,
    exchangeRate: formState.exchangeRate,
    poNumber: formState.poNumber,
    partyVendorCode: formState.partyVendorCode,
    lutNumber: formState.lutNumber,
    lutDate: formState.lutDate,
    portCode: formState.portCode,
    paymentTerms: formState.paymentTerms,
    bankDetails: formState.bankDetails,
    notes: formState.notes,
    termsConditions: formState.termsConditions,
    roundOffAmount: formState.roundOffAmount,
    ...exportFields,
    items: buildItemsPayload(formState.items),
  }
}

export async function submitExistingInvoice(p: SubmitExistingParams): Promise<void> {
  const exportFields = buildSharedExportFields(p.formState)
  const response = p.selectedInvoice.status === 'posted'
    ? await editPostedInvoice(p.organizationId, p.selectedInvoice.id, buildEditPostedPayload(p.formState, exportFields))
    : await updateInvoice(p.organizationId, p.selectedInvoice.id, buildUpdatePayload(p.formState, exportFields))

  if (response.data) {
    p.addToast({
      variant: 'success',
      message: p.selectedInvoice.status === 'posted'
        ? 'Posted invoice updated — journal entry has been reversed and recreated'
        : 'Invoice updated successfully',
    })
    await p.fetchInvoices()
    p.navigate(`${p.basePath}/${p.selectedInvoice.id}`)
  } else if (response.error) {
    throw new Error(response.error.message)
  }
}

interface SubmitNewParams {
  formState: InvoiceFormState
  organizationId: string
  currentLedgerId: string | null
  addToast: (t: Omit<Toast, 'id'>) => void
  fetchInvoices: () => Promise<void>
  navigate: (path: string) => void
  basePath: string
}

export async function submitNewInvoice(p: SubmitNewParams): Promise<void> {
  if (!p.currentLedgerId) { throw new Error('No ledger selected') }
  const exportFields = buildSharedExportFields(p.formState)
  const response = await createInvoice(p.organizationId, p.currentLedgerId, {
    orgId: p.organizationId,
    ledgerId: p.currentLedgerId,
    invoiceType: p.formState.invoiceType,
    supplierBillNumber: p.formState.supplierBillNumber,
    invoiceDate: p.formState.invoiceDate,
    dueDate: p.formState.dueDate,
    partyId: p.formState.partyId,
    partyRole: p.formState.partyRole,
    isWalkIn: p.formState.isWalkIn,
    cashAccountId: p.formState.cashAccountId,
    partyAddressId: p.formState.partyAddressId,
    partyAddress: sanitizeAddress(p.formState.partyAddress),
    placeOfSupply: p.formState.placeOfSupply,
    placeOfSupplyCode: p.formState.placeOfSupplyCode,
    currency: p.formState.currency,
    exchangeRate: p.formState.exchangeRate,
    poNumber: p.formState.poNumber,
    partyVendorCode: p.formState.partyVendorCode,
    lutNumber: p.formState.lutNumber,
    lutDate: p.formState.lutDate,
    portCode: p.formState.portCode,
    paymentTerms: p.formState.paymentTerms,
    bankDetails: p.formState.bankDetails,
    notes: p.formState.notes,
    termsConditions: p.formState.termsConditions,
    roundOffAmount: p.formState.roundOffAmount,
    ...exportFields,
    items: buildItemsPayload(p.formState.items),
  })

  if (response.data) {
    p.addToast({ variant: 'success', message: 'Invoice created successfully' })
    await p.fetchInvoices()
    p.navigate(`${p.basePath}/${response.data.id}`)
  } else if (response.error) {
    throw new Error(response.error.message)
  }
}
